<template>
  <b-col lg="6">
    <b-card>
      <template v-slot:header>
        <img :src="platform.logo" alt="" style="height:60px">
      </template>
      <h4>{{platform.name}}
        <font-awesome-icon class="text-success" icon="check" size="lg" v-if="hasThisIntegration && !integrationHasError" />
        <font-awesome-icon class="text-warning" icon="exclamation-triangle" size="lg" v-if="hasThisIntegration && integrationHasError" />
      </h4>
      <p v-if='integrationHasError' v-translate>There was an error in the integration. Please click in `help` to get support</p>
      <b-form-checkbox v-model="app.webhook_enabled" @input="setEditingWebhook(app); submit()" :disabled="!hasThisIntegration || integrationHasError" :value="1" :unchecked-value="0" switch v-if="app">
        <translate>Webhook Enabled</translate>
      </b-form-checkbox>
      <p :class="!hasExternalAuth && hasThisIntegration ? '' : 'mb-0'">{{platform.description}}</p>
      <b-alert class="mb-0 p-2" variant="info" v-if="!hasExternalAuth && hasThisIntegration" show>
        <dl class="mb-0">
          <dt v-translate>Application ID</dt>
          <dd>{{user.company.applications.find(x => x.platform_id === platforms.find(y => y.slug === platform.slug).id).id}} <a href="#" @click.prevent="copyToClipboard(user.company.applications.find(x => x.platform_id === platforms.find(y => y.slug === platform.slug).id).id, 'appId')" class="text-primary" title="Copy to Clipboard"><font-awesome-icon :icon="['far', 'copy']" size="1x" /></a></dd>
          <dt v-translate>Secret Key</dt>
          <dd>{{user.company.applications.find(x => x.platform_id === platforms.find(y => y.slug === platform.slug).id).key}} <a href="#" @click.prevent="copyToClipboard(user.company.applications.find(x => x.platform_id === platforms.find(y => y.slug === platform.slug).id).key, 'secret')" class="text-primary" title="Copy to Clipboard"><font-awesome-icon :icon="['far', 'copy']" size="1x" /></a></dd>
        </dl>
        <dl v-if="platform.manageWebhook">
          <dt v-translate>Webhook URL</dt>
          <dd>{{app.webhook_url || $gettext('No URL defined yet')}}<span v-if="isCompanyAdmin">&nbsp;&nbsp;&nbsp;<b-button variant="primary" size="sm" style="padding: .1rem .5rem" v-b-modal="'modalEditWebhook' + platform.slug" @click="setEditingWebhook(app)" v-translate>Webhook Configuration</b-button></span></dd>
        </dl>
      </b-alert>
      <div v-if="platform.manualUrl || platform.helpUrl">
        <hr>
        <b-button size="sm" style="padding: 0.1rem 1rem;" router-tag="a" variant="dark" :href="platform.downloadUrl" target="_blank" v-if="platform.downloadUrl">
          <font-awesome-icon icon="download" size="1x" />&nbsp;&nbsp;<translate>Download</translate>
        </b-button>
        <b-button size="sm" style="padding: 0.1rem 1rem;" router-tag="a" variant="dark" :href="platform.manualUrl" target="_blank" v-if="platform.manualUrl">
          <font-awesome-icon icon="file-pdf" size="1x" />&nbsp;&nbsp;<translate>Manual</translate>
        </b-button>
        <b-button size="sm" style="padding: 0.1rem 1rem;" router-tag="a" variant="dark" :href="platform.helpUrl" target="_blank" v-if="platform.helpUrl">
          <font-awesome-icon icon="life-ring" size="1x" />&nbsp;&nbsp;<translate>Help</translate>
        </b-button>
      </div>
      <template v-slot:footer>
        <div v-if="!hasThisIntegration && isShopifyHomologationProcess">
          <div class="text-right" v-if="hasExternalAuth">
            <b-button size="sm" variant="primary" @click="redirectAuth(platform)" v-translate>
                Start Integration
            </b-button>
          </div>
          <div class="text-right" v-else>
            <b-button size="sm" variant="primary" @click="generateApplication(platform.slug)" v-if="isCompanyAdmin" v-translate>
                Generate Application ID &amp; Secret Key
            </b-button>
            <div v-else>
              <b-alert variant="warning" show class="mb-0 text-left">
                <small v-translate>This integration must be set up and managed by your Company Admin user</small>
              </b-alert>
            </div>
          </div>
        </div>
        <b-row v-else>
          <b-col v-if="isShopifyHomologationProcess">
            <b-button size="sm" variant="primary" v-if="hasExternalAuth" @click="redirectAuth(platform)" v-translate>
                Reset Integration
            </b-button>
            <div v-else>
              <b-button size="sm" variant="primary" @click="generateNewKey(user.company.applications.find(x => x.platform_id === platforms.find(y => y.slug === platform.slug).id).id)" v-if="isCompanyAdmin" v-translate>
                  Generate new Secret Key
              </b-button>
              <div v-else>
                <b-alert variant="warning" show class="mb-0 text-left">
                  <small v-translate>This integration must be set up and managed by your Company Admin user</small>
                </b-alert>
              </div>
            </div>
          </b-col>

          <b-col cols="4" class="text-right" v-if="!!hasExternalAuth || isCompanyAdmin">
            <b-button :to="{name: 'integration-settings', params: { slug: platform.slug }}" size="sm" variant="info" style="padding: 0.5rem 0.57rem;" v-if="platform.delete !== 'self'">
              <font-awesome-icon class="text-white" icon="cog" size="lg" />
            </b-button>

            <b-button size="sm" variant="danger" v-b-modal="'confirmDelete' + platform.slug" style="padding: 0.5rem 0.65rem;" v-if="platform.delete === 'self'">
              <font-awesome-icon class="text-white" icon="trash-alt" size="lg" />
            </b-button>
            <b-button :id="'cant-delete-integration-' + platform.slug" class="cant-delete-integration" size="sm" variant="danger" style="padding: 0.5rem 0.65rem;" v-else>
              <font-awesome-icon class="text-white" icon="trash-alt" size="lg" />
            </b-button>
            <b-tooltip :target="'cant-delete-integration-' + platform.slug" triggers="hover" v-if="platform.delete !== 'self'">
              <translate>This integration must be uninstalled from the Control Panel of your Shop</translate>
            </b-tooltip>
          </b-col>
        </b-row>
      </template>
    </b-card>

    <b-modal ok-variant="danger" :id="'confirmDelete' + platform.slug" :ref="'confirmDelete' + platform.slug" @ok="confirmDelete(platform.slug)">
      <div slot="modal-title" v-translate>Alert</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <p v-translate="{reference: platform.name}">Are you sure you want to delete the platform "%{reference}"?</p>
      <div slot="modal-ok" v-translate>Confirm delete!</div>
      <div slot="modal-cancel" v-translate>Cancel</div>
    </b-modal>

    <b-modal :id="'modalNewSecret' + platform.slug" :ref="'modalNewSecret' + platform.slug" :hide-footer="true" @hidden="newKeyData = {}">
      <div slot="modal-title" v-translate>New API Credentials</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <div class="w-100">
        <div class="form-group col-md-12">
          <label class="control-label" v-translate>Application ID</label> <a href="#" v-if="newKeyData.id" @click.prevent="copyToClipboard(newKeyData.id, 'appId')" class="text-secondary" title="Copy to Clipboard"><font-awesome-icon :icon="['far', 'copy']" size="1x" /></a>
          <input class="form-control col-md-12" type="text" v-model="newKeyData.id" id="newKeyData-id" readonly>
        </div>
      </div>
      <div class="form-group col-md-12">
        <label class="control-label" v-translate>Secret Key</label> <a href="#" v-if="newKeyData.key" @click.prevent="copyToClipboard(newKeyData.key, 'secret')" class="text-secondary" title="Copy to Clipboard"><font-awesome-icon :icon="['far', 'copy']" size="1x" /></a>
        <input class="form-control col-md-12" type="text" v-model="newKeyData.key" id="newKeyData-key" readonly>
      </div>
    </b-modal>

    <b-modal :id="'modalEditWebhook' + platform.slug" :ref="'modalEditWebhook' + platform.slug" size="lg" hide-footer @hidden="editingWebhook = {}">
      <div slot="modal-title" v-translate>Edit Webhook</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <form class="" role="form" @submit.prevent="submit">
        <div class="row">
          <h3 class="col-12" v-translate>Notifications' Webhook Definition</h3>
          <b-col cols="12" v-if="!isCompanyAdmin">
            <b-alert variant="warning" show>
              <translate>The Notifications' Webhook data must be managed by your Company Admin user.</translate>
            </b-alert>
          </b-col>
          <h4 class="col-12 mt-3" v-translate>Basic data</h4>
          <div class="form-group col-md-12">
            <b-form-checkbox v-model="editingWebhook.webhook_enabled" :disabled="!isCompanyAdmin" :value="1" :unchecked-value="0" switch>
              <translate>Webhook Enabled</translate>
            </b-form-checkbox>
          </div>
          <div class="form-group col-md-12">
            <label class="control-label" v-translate>Webhook URL</label>
            <input class="form-control col-md-12" type="text" v-model="editingWebhook.webhook_url" :disabled="!isCompanyAdmin">
          </div>
          <div class="form-group col-md-12">
            <label class="control-label" v-translate>Webhook Method</label><br>
            <b-form-select v-model="editingWebhook.webhook_method" class="form-control col-md-4" required :disabled="!isCompanyAdmin">
              <option :value="null" v-translate>Please select an option</option>
              <option value="POST">POST</option>
              <option value="GET">GET</option>
            </b-form-select>
          </div>
          <h4 class="col-12 mt-4" v-translate>Headers</h4>
          <div class="col-md-12">
            <div class="row" v-for="(headerField, index) in editingWebhook.webhook_headers" :key="index">
              <div class="form-group col-md-4">
                <label class="control-label" v-translate>Header Key</label>
                <input class="form-control col-md-12" type="text" v-model="editingWebhook.webhook_headers[index].headerKey" :required="editingWebhook.webhook_headers[index].headerValue.length > 0" :disabled="!isCompanyAdmin">
              </div>
              <div class="form-group col-md-6">
                <label class="control-label" v-translate>Header Value</label>
                <input class="form-control col-md-12" type="text" v-model="editingWebhook.webhook_headers[index].headerValue" :required="editingWebhook.webhook_headers[index].headerKey.length > 0" :disabled="!isCompanyAdmin">
              </div>
              <div class="form-group col-md-2" v-if="index !== 0">
                <label class="control-label text-white">&nbsp;</label><br>
                <b-button variant="primary" size="sm" @click="editingWebhook.webhook_headers.splice(index, 1)" v-translate :disabled="!isCompanyAdmin">Remove</b-button>
              </div>
            </div>
            <div class="text-right">
              <b-button variant="primary" size="sm" @click="editingWebhook.webhook_headers.push(Object.assign({}, headerModel))" v-translate :disabled="!isCompanyAdmin">Add header</b-button>
            </div>
          </div>
        </div>
        <div class="row text-left">
          <div class="form-group col-12 form-actions">
            <label class="col-md-3 control-label"></label>
            <div class="col-md-8">
              <b-button variant="primary" size="lg" type="submit" v-translate :disabled="!isCompanyAdmin">Save Changes</b-button>
            </div>
          </div>
        </div>
      </form>
    </b-modal>
  </b-col>
</template>

<script>
import { mapGetters } from 'vuex'
import { APPLICATION_DELETE, PLATFORM_DELETE, FETCH_PROFILE } from '@/store/actions.type'

export default {
  props: {
    platform: {
      type: Object,
      default: {}
    },
    platforms: {
      type: Array,
      default: []
    }
  },
  data() {
    return {
      newKeyData: {},
      app: {},
      headerModel: {
        headerKey: '',
        headerValue: ''
      },
      editingWebhook: {
        webhook_enabled: 1,
        webhook_url: null,
        webhook_method: null,
        webhook_headers: []
      }
    }
  },
  computed: {
    isShopifyHomologationProcess() {
      // Only enable during a shopify homologation process:
      return true
      // if (this.platform.slug.indexOf('shopify') > -1) {
      //   return !(window.location.origin.indexOf('urbano') > -1)
      // }
      // return true
    },
    hasExternalAuth() {
      return this.platform.to || this.platform.href
    },
    hasThisIntegration() {
      const hasValidData = this.user && this.user.platform_user_authorizations && this.user.company.applications && this.platforms
      if (!hasValidData) return false
      else if (this.platform.microserviceIntegration) {
        return this.getMicroserviceApp() !== null
      } else if (this.platform.href || this.platform.to) {
        return this.user.platform_user_authorizations.findIndex(x => x.platform.slug === this.platform.slug) !== -1
      }
      return this.user.company.applications.findIndex(x => x.platform_id === this.platforms.find(y => y.slug === this.platform.slug).id) !== -1
    },
    integrationHasError() {
      const currentIntegration = this.user.platform_user_authorizations.findIndex(x => x.platform.slug === this.platform.slug)
      return currentIntegration !== -1 && this.user.platform_user_authorizations[currentIntegration].metadata && this.user.platform_user_authorizations[currentIntegration].metadata.error
    },
    isCompanyAdmin() {
      return this.$store.getters.profile.company && this.$store.getters.profile.roles.findIndex(x => x.name === 'company-admin') !== -1
    },
    ...mapGetters([
      'user'
    ])
  },
  mounted() {
    this.app = this.user.company.applications.find(x => x.platform_id === this.platforms.find(y => y.slug === this.platform.slug).id)
  },
  methods: {
    redirectAuth(platform) {
      if (platform.to) return this.$router.push(platform.to)
      const url = this.getIntegrationUrl(platform)
      window.location.href = url
    },
    getIntegrationUrl(platform) {
      const platformSlug = platform.slug
      if (!this.$appConfig.integrations[platformSlug]) {
        return platform.href
      }
      return this.platform.href
        .replace('%appId%', this.$appConfig.integrations[platformSlug].appId)
        .replace('%state%', window.location.hostname)
        .replace('%countryDomain%', this.$frontConfig.domain)
    },
    getMicroserviceApp() {
      for (const app of this.user.company.applications) {
        if (app.platform && app.platform.slug === this.platform.slug && app.webhook_url && app.id && app.key) {
          const urlObj = new URL(app.webhook_url)
          const params = new URLSearchParams(urlObj.search)
          if (params.get('userId') === String(this.user.id)) return app
        }
      }
      return null
    },
    async confirmDelete(slug) {
      if (this.platform.microserviceIntegration) {
        const application = this.getMicroserviceApp()
        if (!application) return this.refreshIntegration()
        await this.deleteMicroservice(application)
        await this.$store.dispatch(APPLICATION_DELETE, application.id).then(() => this.refreshIntegration())
      } else if (this.platform.href) {
        this.$store.dispatch(PLATFORM_DELETE, slug).then(() => this.refreshIntegration())
      } else {
        this.$store.dispatch(APPLICATION_DELETE, this.user.company.applications.find(x => x.platform_id === this.platforms.find(y => y.slug === this.platform.slug).id).id)
          .then(response => {
            this.refreshIntegration()
          })
      }
    },
    async deleteMicroservice(application) {
      const uninstall = this.platform.microserviceUninstall
      if (uninstall) {
        const integration = this.$appConfig.integrations[uninstall.integration]
        const headers = { 'x-api-key': integration.header, Auth: application.key }
        await this.$http.post(`${integration.baseUrl}${uninstall.path}?appId=${application.id}`, {}, { headers })
        return
      }
      const path = this.$appConfig.integrations.ecommerce.baseUrl + this.platform.slug + '/uninstall'
      const headers = { 'x-api-key': this.$appConfig.integrations.ecommerce.header, Auth: application.key }
      await this.$http.delete(`${path}?appId=${application.id}`, { headers })
    },

    generateApplication(slug) {
      const thePlatform = this.platforms.find(x => x.slug === slug)
      const payload = {
        name: this.user.company.name + ' ' + thePlatform.name,
        company_id: this.user.company_id,
        platform_id: thePlatform.id,
        active: true
      }

      this.$http.post(this.$appConfig.api.baseUrl + 'applications', payload).then(response => {
        this.newKeyData = response.data
        this.refreshIntegration()
      })
    },
    generateNewKey(appId) {
      if (confirm(this.$gettext('Are you sure COMPLETELY SURE you want to generate a NEW SECRET KEY?\n\nIMPORTANT: Remember that your API integration WILL STOP WORKING IMMEDIATELY with the current secret key and you will need to make the necessary changes in your own app/system/integration to get it back to work.\n\nThis operation CANNOT BE UNDONE.')) === true) {
        this.$bvModal.show('modalNewSecret' + this.platform.slug)
        this.$http.post(this.$appConfig.api.baseUrl + 'applications/' + appId + '/generate-api-key')
          .then(response => {
            this.newKeyData = response.data
            this.refreshIntegration()
          })
      }
    },
    refreshIntegration() {
      this.$store.dispatch(FETCH_PROFILE)
        .then(response => {
          const payload = { profile: this.user }
          this.$store.commit('setProfile', payload)
          this.app = this.user.company.applications.find(x => x.platform_id === this.platforms.find(y => y.slug === this.platform.slug).id)
        })
    },
    setEditingWebhook(app) {
      this.editingWebhook = {
        id: null,
        webhook_enabled: 1,
        webhook_url: null,
        webhook_method: null,
        webhook_headers: []
      }
      this.editingWebhook.id = app.id
      this.editingWebhook.webhook_enabled = app.webhook_enabled
      this.editingWebhook.webhook_url = app.webhook_url
      this.editingWebhook.webhook_method = app.webhook_method
      const that = this
      const currentHeaders = app.webhook_headers || []
      if (currentHeaders.length > 0) {
        currentHeaders.forEach(function (val) {
          that.editingWebhook.webhook_headers.push({
            headerKey: Object.keys(val)[0],
            headerValue: Object.values(val)[0]
          })
        })
      } else {
        if (!app.webhook_headers || app.webhook_headers.length === 0) {
          this.editingWebhook.webhook_headers = [{
            headerKey: '',
            headerValue: ''
          }]
        }
      }
    },
    submit() {
      const headers = []
      this.editingWebhook.webhook_headers.forEach(function (val) {
        if (val.headerKey) {
          const obj = {}
          obj[val.headerKey] = val.headerValue
          headers.push(obj)
        }
      })

      const params = {
        webhook_enabled: this.editingWebhook.webhook_enabled,
        webhook_url: this.editingWebhook.webhook_url,
        webhook_method: this.editingWebhook.webhook_method,
        webhook_headers: this.editingWebhook.webhook_headers[0].headerKey.length > 0 ? headers : null
      }
      this.$http.patch(this.$appConfig.api.baseUrl + 'companies/' + this.user.company.id + '/applications/' + this.editingWebhook.id, params)
        .then(response => {
          this.editingWebhook = {
            id: null,
            webhook_enabled: 1,
            webhook_url: null,
            webhook_method: null,
            webhook_headers: []
          }
          this.refreshIntegration()
          this.$refs['modalEditWebhook' + this.platform.slug].hide()
        })
    },
    copyToClipboard(str, type = 'id') {
      const el = document.createElement('textarea')
      el.value = str
      el.setAttribute('readonly', '')
      el.style.position = 'absolute'
      el.style.left = '-9999px'
      document.body.appendChild(el)
      el.select()
      try {
        document.execCommand('copy')
        let mssg
        switch (type) {
          case 'appId':
            mssg = 'The application ID has been copied to Clipboard'
            break
          case 'secret':
            mssg = 'The secret key has been copied to Clipboard'
            break
          case 'email':
            mssg = 'The e-mail has been copied to Clipboard'
            break
          default:
            mssg = 'The ID has been copied to Clipboard'
        }
        this.makeToast(this.$gettext('Notice'), mssg, 'success', false, 2000)
      } catch (e) {
        let mssg
        switch (type) {
          case 'appId':
            mssg = 'The application ID could NOT be copied to Clipboard'
            break
          case 'secret':
            mssg = 'The secret key could NOT be copied to Clipboard'
            break
          case 'email':
            mssg = 'The e-mail could NOT be copied to Clipboard'
            break
          default:
            mssg = 'The ID could NOT be copied to Clipboard'
        }
        this.makeToast(this.$gettext('Warning'), mssg, 'danger', false, 2000)
      }
      document.body.removeChild(el)
    },
    makeToast(title, mssg, variant = 'info', append = false, autoHideDelay = false, solid = true) {
      const options = {
        title,
        appendToast: append,
        variant,
        solid
      }
      if (autoHideDelay !== false) {
        options.autoHideDelay = autoHideDelay
      } else {
        options.noAutoHide = true
      }

      this.$bvToast.toast([mssg], options)
    }
  }
}
</script>

<style lang="scss">
.cant-delete-integration {
  color: #fff;
  background-color: #dc3545;
  border-color: #dc3545;
  opacity: 0.65;
  box-shadow: none !important;
}
</style>
